import React from 'react';

export function TextField({ label, value, onChange, mono }: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) {
  return (
    <label className="block">
      {label && <span className="block text-xs text-gray-500 mb-0.5">{label}</span>}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border border-gray-300 rounded-md px-2 py-1 text-sm ${mono ? 'font-mono text-xs' : ''}`}
      />
    </label>
  );
}

export function TextArea({ label, value, onChange, rows = 3 }: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="block">
      {label && <span className="block text-xs text-gray-500 mb-0.5">{label}</span>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
      />
    </label>
  );
}

/** Edits a string list as one-item-per-line text. */
export function LinesEditor({ label, values, onChange, rows = 3 }: {
  label?: string;
  values: string[];
  onChange: (v: string[]) => void;
  rows?: number;
}) {
  return (
    <TextArea
      label={label}
      value={values.join('\n')}
      rows={rows}
      onChange={(v) => onChange(v.split('\n').filter((line) => line.trim() !== ''))}
    />
  );
}

export function Chip({ children, tone = 'gray' }: {
  children: React.ReactNode;
  tone?: 'gray' | 'indigo' | 'green' | 'amber' | 'red' | 'blue';
}) {
  const tones: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-800',
    red: 'bg-red-50 text-red-700',
    blue: 'bg-blue-50 text-blue-700',
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`p-3 bg-white border border-gray-200 rounded-md ${className}`}>{children}</div>
  );
}

export function SmallButton({ children, onClick, disabled, tone = 'default', title }: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'primary' | 'green' | 'danger';
  title?: string;
}) {
  const tones: Record<string, string> = {
    default: 'border border-gray-300 hover:bg-gray-50 text-gray-700',
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    green: 'bg-green-600 hover:bg-green-700 text-white',
    danger: 'border border-red-300 hover:bg-red-50 text-red-700',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`px-2.5 py-1.5 rounded-md text-sm disabled:opacity-50 ${tones[tone]}`}
    >
      {children}
    </button>
  );
}
